import {Component, EventEmitter, Input, Output, ViewChild, ChangeDetectorRef, AfterViewInit, ContentChild} from '@angular/core';

import {WizardModalComponent} from '../../../../../../shared/components';
import {Log} from "ng2-logger";
import {LineDetailsFormComponent} from './line-details-form/line-details-form.component';
import {LineEquipmentFormComponent} from './line-equipment-form/line-equipment-form.component';
import {LineInsertionCyclesFormComponent} from './line-insertion-cycles-form/line-insertion-cycles-form.component';
import { LineTransportSettingsFormComponent } from './line-transport-settings-form/line-transport-settings-form.component';

import {Line} from '@app/shared/models/line.model';

import * as _ from 'lodash';
import {PermissionService} from '@app/authentication/services/permission.service';
import {TranslateService} from '@ngx-translate/core';

enum LineSteps {
    DETAILS = 0,
    TRANSPORT = 1,
    INSERTION_CYCLES = 2,
    EQUIPMENT = 3
}

/**
 * Modal form for creating / updating {@link Line} objects.
 */
@Component({
    selector: 'app-line-create-modal',
    templateUrl: './line-create-modal.component.html',
    styleUrls: ['./line-create-modal.component.scss']
})
export class LineCreateModalComponent implements AfterViewInit {
    _logger = Log.create(LineCreateModalComponent.name);
    isSPI = false;

    @ViewChild(WizardModalComponent, {static: false}) wizardModal: WizardModalComponent;
    // 1st step
    @ViewChild(LineDetailsFormComponent, {static: false}) lineDetailsForm: LineDetailsFormComponent;
    // 2nd Step
    @ViewChild(LineInsertionCyclesFormComponent, {static: false}) lineInsertionCyclesForm: LineInsertionCyclesFormComponent;
    // 3rd Step
    @ViewChild(LineTransportSettingsFormComponent, {static: false}) lineTransportSettingsForm: LineTransportSettingsFormComponent;
    // 4th Step
    @ViewChild(LineEquipmentFormComponent, {static: false}) lineEquipmentForm: LineEquipmentFormComponent;

    @Input() isLoading = false;
    @Input() isReadyOnly: boolean;
    @Output() onCreate: EventEmitter<any> = new EventEmitter();
    @Output() onUpdate: EventEmitter<any> = new EventEmitter();
    @Output() onDelete: EventEmitter<any> = new EventEmitter();
    @Output() onClose: EventEmitter<any> = new EventEmitter();

    public line: Line;
    public deleteTooltipText = '';
    private duplicateMode = false;
    private previousLineName = '';
    private selectedStepIndex = 0;

    get lineDetailsValid() {
        return this.lineDetailsForm ? this.lineDetailsForm.isFormValid : false;
    }

    get lineTransportValid() {
        return this.lineTransportSettingsForm ? this.lineTransportSettingsForm.isFormValid : false;
    }

    get lineInsertionCyclesValid() {
        return this.lineInsertionCyclesForm ? this.lineInsertionCyclesForm.isFormValid : false;
    }

    get isEditMode() {
        return !!this.line && !!this.line.id && !this.duplicateMode;
    }

    constructor(private cdr: ChangeDetectorRef, private permissionService: PermissionService, private translate: TranslateService) {
    }

    ngAfterViewInit(): void {
        this.cdr.markForCheck();
    }

    async onWizardStepChange(currentIndex) {
        // logic for the old step
        let formData = {};
        if (!this.isSPI && currentIndex === LineSteps.INSERTION_CYCLES) {
            currentIndex = LineSteps.EQUIPMENT;
        }

        switch (this.selectedStepIndex) {
            case LineSteps.DETAILS:
                formData = this.lineDetailsForm.getFormDetails();
                break;
            case LineSteps.TRANSPORT:
                formData = this.lineTransportSettingsForm.getFormTransportData();
                break;
            case LineSteps.INSERTION_CYCLES:
                formData = this.lineInsertionCyclesForm.getFormInsertionCycles();
                break;
            case LineSteps.EQUIPMENT:
                formData = this.lineEquipmentForm.getFormEquipments();
                break;
            default:
                break;
        }

        Object.assign(this.line, formData);

        // logic for the new step
        switch (currentIndex) {
            case LineSteps.DETAILS:
                await this.lineDetailsForm.init(this.line);
                break;
            case LineSteps.TRANSPORT:
                this.lineTransportSettingsForm.init(this.line);
                break;
            case LineSteps.INSERTION_CYCLES:
                this.lineInsertionCyclesForm.init(this.line);
                break;
            case LineSteps.EQUIPMENT:
                this.lineEquipmentForm.init(this.line);
                break;
            default:
                break;
        }
        this.selectedStepIndex = currentIndex;
    }

    onWizardSubmit() {
        this.isEditMode ? this.updateLine() : this.createLine();
    }

    onWizardCancel() {
        this.close();
    }

    createLine() {
        if (!this.lineDetailsForm.isFormValid || (this.isSPI && !this.lineInsertionCyclesForm.isFormValid)) {
            return;
        }
        this.onCreate.emit(this.line);
    }

    updateLine() {
        if (!this.lineDetailsForm.isFormValid && (this.isSPI && !this.lineInsertionCyclesForm.isFormValid)) {
            return;
        }
        this.onUpdate.emit({previousLineName: this.previousLineName, newLine: this.line});
    }

    deleteLine() {
        this.onDelete.emit(this.line);
    }

    open(line?, duplicateMode = false) {
        this.line = _.cloneDeep(line) || new Line();
        this.duplicateMode = duplicateMode;
        this.wizardModal.open();
    }

    async onModalOpened() {
        this.previousLineName = this.line.name;
        await this.lineDetailsForm.init(this.line);
        this.translateDeleteTooltipText();
    }

    close() {
        this.reset();
        this.wizardModal.close();
        this.onClose.emit(this.line);
    }

    reset() {
        this.line = new Line();
        this.duplicateMode = false;
        this.selectedStepIndex = 0;
        this.lineDetailsForm.resetForm();
        this.lineEquipmentForm.resetForm();
        this.wizardModal.reset();

        if (this.isSPI) {
            this.lineInsertionCyclesForm.resetForm();
        }
    }

    isLineInUse() {
        return this.line.inUse;
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_LINES');
    }

    translateDeleteTooltipText() {
        this.isEditMode && this.shouldRenderModifiers() && !this.isLineInUse() ?
            this.deleteTooltipText = this.translate.instant('admin.lines.create-modal.button.deleteTooltipInUse') :
            this.deleteTooltipText = this.translate.instant('admin.lines.create-modal.button.deleteTooltip')
    }

    onSpiValueChanged(value) {
        if (!value && this.lineInsertionCyclesForm) {
            this.line.insertionCycles = [];
            this.lineInsertionCyclesForm.resetForm();
        }
        this.isSPI = value;
    }
}
